import { useEffect, useRef, useState } from "react";
import { type AxiosInstance, isAxiosError } from "axios";
import {
  type ParsedFile,
  type Mapping,
  type InputRow,
  type MatchedRow,
  type Settings,
  type PendingReceipt,
} from "./types";
import {
  getCorporationListAcceptance,
  getCorporationListSample,
  getCorporationListSettings,
  matchCorporationList,
  parseCorporationListFile,
  requestCorporationList,
} from "../api/corporationList";
import { detectMapping, mapRows, validMapping, markDuplicates } from "./rows";
import { errorMessage } from "./messages";
import { readPending, savePending, removePending } from "./pending";

export interface CorporationListState {
  file: ParsedFile | null;
  filename: string;
  fileSize: number;
  mapping: Mapping;
  inputs: InputRow[];
  rows: MatchedRow[];
  matchingRequired: boolean;
  settings: Settings | null;
  busy: boolean;
  sending: boolean;
  error: string;
  pending: PendingReceipt | null;
  upload: (file: File) => Promise<void>;
  uploadSample: () => Promise<void>;
  clearFile: () => void;
  changeMapping: (mapping: Mapping) => void;
  confirmMapping: () => Promise<boolean>;
  changeRow: (rowNumber: number, change: Partial<InputRow>) => Promise<boolean>;
  changeRows: (
    rowNumbers: number[],
    change: Partial<InputRow>
  ) => Promise<boolean>;
  retryMatching: () => Promise<void>;
  submit: () => Promise<void>;
  lookup: () => Promise<void>;
}

export const useCorporationList = (
  apiClient: AxiosInstance,
  userId: string,
  onAccepted: () => void
): CorporationListState => {
  const api = useRef(apiClient);
  api.current = apiClient;
  const generation = useRef(0);
  const controller = useRef<AbortController>();
  const submitLock = useRef(false);
  const activeUser = useRef<string | null>(userId);
  const [file, setFile] = useState<ParsedFile | null>(null);
  const [filename, setFilename] = useState("");
  const [fileSize, setFileSize] = useState(0);
  const [mapping, setMapping] = useState<Mapping>({});
  const [inputs, setInputs] = useState<InputRow[]>([]);
  const [rows, setRows] = useState<MatchedRow[]>([]);
  const [matchingRequired, setMatchingRequired] = useState(false);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [pending, setPending] = useState<PendingReceipt | null>(() =>
    readPending(userId)
  );

  useEffect(() => {
    activeUser.current = userId;
    setPending(readPending(userId));
    setFile(null);
    setInputs([]);
    setRows([]);
    setMatchingRequired(false);
    const abort = new AbortController();
    void getCorporationListSettings(api.current, abort.signal)
      .then((data) => {
        setSettings(data);
      })
      .catch((error: unknown) => {
        if (!abort.signal.aborted) setError(errorMessage(error));
      });
    return () => {
      activeUser.current = null;
      abort.abort();
      generation.current += 1;
      controller.current?.abort();
    };
  }, [userId]);

  const begin = (): [number, AbortSignal] => {
    controller.current?.abort();
    controller.current = new AbortController();
    generation.current += 1;
    setBusy(true);
    setError("");
    return [generation.current, controller.current.signal];
  };
  const match = async (
    nextInputs: InputRow[],
    nextMapping: Mapping,
    partial = false
  ): Promise<boolean> => {
    const [version, signal] = begin();
    setInputs(nextInputs);
    const selected = partial
      ? nextInputs.filter(
          (input) =>
            !input.explicitlyExcluded &&
            rows.some(
              (row) =>
                row.rowNumber === input.rowNumber && row.targetState === "HELD"
            )
        )
      : nextInputs;
    try {
      if (selected.length === 0) return false;
      setMatchingRequired(true);
      const data = await matchCorporationList(
        api.current,
        nextMapping,
        selected,
        signal
      );
      if (version !== generation.current) return false;
      const incoming = new Map(data.rows.map((row) => [row.rowNumber, row]));
      setRows(
        markDuplicates(
          partial
            ? rows.map((row) => incoming.get(row.rowNumber) ?? row)
            : data.rows
        )
      );
      setMatchingRequired(false);
      return true;
    } catch (error) {
      if (version === generation.current && !signal.aborted)
        setError(errorMessage(error));
      return false;
    } finally {
      if (version === generation.current) setBusy(false);
    }
  };
  /**
   * ファイルを用意して解析する。用意している間（サンプルの取得中など）も処理中として扱い、
   * 後から別の操作が入って番号が進んだら、遅れて届いた結果で上書きしない
   */
  const read = async (
    source: File | ((signal: AbortSignal) => Promise<File>)
  ): Promise<void> => {
    const [version, signal] = begin();
    setFile(null);
    setInputs([]);
    setRows([]);
    setMatchingRequired(false);
    setFilename("");
    setFileSize(0);
    setMapping({});
    try {
      const selected = source instanceof File ? source : await source(signal);
      if (version !== generation.current) return;
      setFilename(selected.name);
      setFileSize(selected.size);
      if (
        !/\.(csv|xlsx)$/i.test(selected.name) ||
        selected.size > (settings?.maxFileBytes ?? 10_000_000)
      )
        throw new Error("FILE_LIMIT");
      const data = await parseCorporationListFile(
        api.current,
        selected,
        signal
      );
      if (version !== generation.current) return;
      // 照合は列のマッピングで「読み込んで法人を確認」を押したときに行う。見出しから判定した列は初期値にするだけ
      setFile(data);
      setMapping(detectMapping(data));
    } catch (error) {
      if (version === generation.current && !signal.aborted)
        setError(
          error instanceof Error && error.message === "FILE_LIMIT"
            ? "10MB以内のCSVまたはXLSXファイルを選択してください。"
            : errorMessage(error)
        );
    } finally {
      if (version === generation.current) setBusy(false);
    }
  };
  // サンプルも選んだファイルと同じ流れに乗せ、取込からの操作をそのまま試せるようにする
  const uploadSample = async (): Promise<void> => {
    await read(async (signal) => {
      const data = await getCorporationListSample(api.current, "xlsx", signal);
      return new File([data], "法人リストサンプル.xlsx");
    });
  };
  const changeMapping = (next: Mapping): void => {
    generation.current += 1;
    controller.current?.abort();
    setBusy(false);
    setMapping(next);
    setRows([]);
    setMatchingRequired(false);
    setInputs([]);
  };
  const clearFile = (): void => {
    generation.current += 1;
    controller.current?.abort();
    setBusy(false);
    setError("");
    setFile(null);
    setFilename("");
    setFileSize(0);
    setMapping({});
    setInputs([]);
    setRows([]);
    setMatchingRequired(false);
  };
  const confirmMapping = async (): Promise<boolean> => {
    if (!file || !validMapping(mapping)) return false;
    // inputs は upload・changeMapping で必ず空になるため、残っていれば現在の列対応で作った
    // 編集済み入力である。照合失敗後の再照合で手入力の番号や除外指定を失わないよう優先する
    return await match(
      inputs.length > 0 ? inputs : mapRows(file, mapping),
      mapping
    );
  };
  const changeRow = async (
    rowNumber: number,
    change: Partial<InputRow>
  ): Promise<boolean> => {
    return await match(
      inputs.map((row) =>
        row.rowNumber === rowNumber ? { ...row, ...change } : row
      ),
      mapping
    );
  };
  const changeRows = async (
    rowNumbers: number[],
    change: Partial<InputRow>
  ): Promise<boolean> => {
    const targets = new Set(rowNumbers);
    return await match(
      inputs.map((row) =>
        targets.has(row.rowNumber) ? { ...row, ...change } : row
      ),
      mapping
    );
  };
  const retryMatching = async (): Promise<void> => {
    await match(inputs, mapping, !matchingRequired);
  };

  const finishAcceptance = (): void => {
    if (activeUser.current !== userId) return;
    removePending(userId);
    setPending(null);
    onAccepted();
  };
  const lookup = async (): Promise<void> => {
    if (!pending || submitLock.current) return;
    submitLock.current = true;
    setSending(true);
    setError("");
    try {
      await getCorporationListAcceptance(api.current, pending.idempotencyKey);
      finishAcceptance();
    } catch (error) {
      setError(
        isAxiosError(error) && error.response?.status === 404
          ? "まだ受付を確認できません。同じキーを保持しています。時間をおいて再確認するか、同じ内容で再送してください。"
          : errorMessage(error)
      );
    } finally {
      submitLock.current = false;
      setSending(false);
    }
  };
  const submit = async (): Promise<void> => {
    if (
      submitLock.current ||
      busy ||
      matchingRequired ||
      !settings?.acquisitionEnabled
    )
      return;
    const targets = rows.flatMap((row) =>
      row.targetState === "TARGET" && row.corporation
        ? [
            {
              rowNumber: row.rowNumber,
              corporateIdentificationNumber:
                row.corporation.corporateIdentificationNumber,
            },
          ]
        : []
    );
    if (targets.length === 0) return;
    submitLock.current = true;
    setSending(true);
    setError("");
    try {
      const receipt = pending ?? {
        idempotencyKey: crypto.randomUUID(),
        sentAt: new Date().toISOString(),
      };
      savePending(userId, receipt);
      setPending(receipt);
      await requestCorporationList(
        api.current,
        receipt.idempotencyKey,
        settings.unitPrice,
        targets
      );
      finishAcceptance();
    } catch (error) {
      // 応答不明の依頼を再送した場合は、今回が拒否されても前回の受付が確定する可能性がある
      const status = isAxiosError(error) ? error.response?.status : undefined;
      if (
        !pending &&
        activeUser.current === userId &&
        (status === 400 || status === 409)
      ) {
        removePending(userId);
        setPending(null);
      }
      setError(errorMessage(error));
      if (!pending && status === 409 && activeUser.current === userId) {
        const abort = new AbortController();
        try {
          const latest = await getCorporationListSettings(
            api.current,
            abort.signal
          );
          if (activeUser.current !== userId) return;
          setSettings(latest);
          if (latest.unitPrice !== settings.unitPrice) {
            setError(
              "取得単価が変更されました。新しい金額を確認して、もう一度取得を依頼してください。"
            );
          }
        } catch (refreshError) {
          if (activeUser.current === userId)
            setError(errorMessage(refreshError));
        }
      }
    } finally {
      submitLock.current = false;
      setSending(false);
    }
  };
  return {
    file,
    filename,
    fileSize,
    mapping,
    inputs,
    rows,
    matchingRequired,
    settings,
    busy,
    sending,
    error,
    pending,
    upload: read,
    uploadSample,
    clearFile,
    changeMapping,
    confirmMapping,
    changeRow,
    changeRows,
    retryMatching,
    submit,
    lookup,
  };
};
